import React, {
  forwardRef, memo, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState,
} from "react";
import { createPortal } from "react-dom";
import styled, { keyframes } from "styled-components";
import { FiPlus, FiChevronRight } from "react-icons/fi";
import { tokens } from "../../activity-logs/styles";
import { tokens as T, hairline } from "../../../styles/designTokens";
import { SANS } from "../../creditos/styles";
import {
  MONTHS, branchLabel, conformityColor, formatCnpj, formatCount, formatMonthKey, formatPercent,
} from "./revisorData";

/**
 * Mapa de cobertura: estabelecimento × mês, um mês por coluna, do primeiro ao
 * último analisado, no acabamento do iOS (cantos arredondados, quatro tons de
 * verde, balão com seta).
 *
 * Desempenho: são ~350 células. Para o hover não re-renderizar a grade:
 * - todo o estilo é estático, numa única folha (classes e data-attributes);
 * - um só listener na grade (delegação) identifica a célula pelo data-*;
 * - o destaque de linha e coluna é uma regra CSS reescrita num <style> próprio;
 * - a faixa da coluna se move por transform, direto no DOM;
 * - o balão é um componente isolado, atualizado por ref.
 */

const LEVELS = [0.32, 0.52, 0.74, 1];
const LABEL_W = 150;
const GREEN = "#10b981";
const RED = "#ef4444";
const TIP_HALF = 118;
const TIP_ROOM = 330; // barra fixa do topo + altura do balão

const level = (docs, max) => {
  if (!docs) return 0;
  const r = Math.sqrt(docs / max);
  if (r > 0.72) return 3;
  if (r > 0.48) return 2;
  if (r > 0.24) return 1;
  return 0;
};

const dk = (theme) => tokens.dk(theme);
const quiet = ({ theme }) => (dk(theme) ? "rgba(255,255,255,0.035)" : "rgba(15,23,42,0.035)");
const neutral = ({ theme }) => (dk(theme) ? "rgba(118,118,128,0.22)" : "rgba(118,118,128,0.14)");

const fadeIn = keyframes`from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: none; }`;

// Uma folha só para a grade inteira: nada aqui depende de props por célula
const Grid = styled.div`
  position: relative;
  display: grid;
  grid-template-columns: ${LABEL_W}px repeat(${({ $cols }) => $cols}, minmax(28px, 1fr));
  column-gap: 4px;
  row-gap: 4px;
  min-width: ${({ $cols }) => LABEL_W + $cols * 32}px;
  font-size: 0.7em;
  color: ${({ theme }) => tokens.label2(theme)};
  animation: ${fadeIn} 360ms ease-out both;
  @media (prefers-reduced-motion: reduce) { animation: none; }

  .y {
    grid-row: 1;
    display: flex;
    align-items: center;
    gap: 8px;
    padding-bottom: 4px;
    font-size: 1.04em;
    font-weight: 600;
    letter-spacing: -0.01em;
    color: ${({ theme }) => theme.text};
  }
  .y::after { content: ""; flex: 1; height: 1px; background: ${({ theme }) => hairline(theme)}; }

  .m {
    grid-row: 2;
    justify-self: center;
    height: 18px;
    padding: 0 6px;
    letter-spacing: -0.01em;
    white-space: nowrap;
    border-radius: 999px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 500;
    transition: background 140ms ease, color 140ms ease;
  }
  .m.gap { color: ${({ theme }) => tokens.label3(theme)}; }

  .lbl {
    display: flex;
    align-items: center;
    gap: 7px;
    min-width: 0;
    padding-right: 12px;
    transition: opacity 140ms ease;
  }
  .lbl .b { color: ${({ theme }) => theme.text}; font-weight: 500; font-size: 1.1em; letter-spacing: -0.01em; white-space: nowrap; }
  .lbl .c { font-size: 0.98em; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; letter-spacing: 0.01em; }

  .cell {
    position: relative;
    z-index: 1;
    height: 24px;
    padding: 0;
    border: none;
    border-radius: 7px;
    cursor: pointer;
    transition: transform 160ms cubic-bezier(0.2, 0.9, 0.25, 1.2), opacity 140ms ease;
  }
  .cell:focus-visible { outline: none; box-shadow: 0 0 0 2px ${({ theme }) => (dk(theme) ? "#0d0d0f" : "#fff")}, 0 0 0 4px ${T.brand}; }

  .hit { background: ${GREEN}; }
  .hit.l0 { opacity: ${LEVELS[0]}; }
  .hit.l1 { opacity: ${LEVELS[1]}; }
  .hit.l2 { opacity: ${LEVELS[2]}; }
  .hit.l3 { opacity: ${LEVELS[3]}; }
  .hit:hover { transform: scale(1.14); z-index: 2; opacity: 1; }

  .miss { background: ${neutral}; }
  .miss::after {
    content: "";
    position: absolute;
    left: 30%;
    right: 30%;
    top: 50%;
    height: 1.5px;
    margin-top: -0.75px;
    border-radius: 1px;
    background: ${({ theme }) => tokens.label3(theme)};
  }
  .miss:hover { transform: scale(1.1); z-index: 2; }

  .gap-cell {
    background: ${quiet};
    color: ${T.brand};
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .gap-cell svg { opacity: 0; font-size: 12px; stroke-width: 3; transition: opacity 140ms ease; pointer-events: none; }
  .gap-cell:hover { background: ${T.brandSoft}; box-shadow: inset 0 0 0 1px ${T.brandRing}; transform: scale(1.1); z-index: 2; }
  .gap-cell:hover svg { opacity: 1; }

  .divider { grid-column: 1 / -1; height: 1px; margin-top: 4px; background: ${({ theme }) => hairline(theme)}; }

  .pct {
    position: relative;
    z-index: 1;
    height: 30px;
    padding: 6px 0 0;
    border: none;
    background: none;
    cursor: pointer;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
    font-family: inherit;
    font-size: 0.95em;
    font-weight: 600;
    letter-spacing: -0.01em;
    transition: opacity 140ms ease;
  }
  .pct * { pointer-events: none; }
  .pct .track { width: 70%; max-width: 26px; height: 3px; border-radius: 2px; background: ${neutral}; overflow: hidden; }
  .pct .track b { display: block; height: 100%; border-radius: 2px; }
  .pct:focus-visible { outline: none; box-shadow: 0 0 0 2px ${T.brand}; border-radius: 6px; }

  /* Faixa da coluna em foco: movida por transform, sem re-render */
  .band {
    position: absolute;
    top: 0;
    left: 0;
    width: 0;
    border-radius: 10px;
    background: ${({ theme }) => (dk(theme) ? "rgba(16,185,129,0.08)" : "rgba(16,185,129,0.07)")};
    pointer-events: none;
    opacity: 0;
    transition: opacity 140ms ease;
    will-change: transform;
  }
  .band.on { opacity: 1; }
`;

const Scroller = styled.div`
  overflow-x: auto;
  padding: 4px 2px 6px;
  margin: 0 -2px;
  scrollbar-width: thin;
  font-family: ${SANS};
  font-variant-numeric: tabular-nums;
  -webkit-font-smoothing: antialiased;
`;

// Regras do foco, reescritas a cada hover: atenua o resto e acende linha e coluna
const focusCss = (scope, col, row) => {
  const s = `.${scope}`;
  const keep = row == null ? `${s} .c${col}` : `${s} .c${col}, ${s} .r${row}`;
  const lv = (n) => (row == null ? `${s} .hit.c${col}.l${n}` : `${s} .hit.c${col}.l${n}, ${s} .hit.r${row}.l${n}`);
  return `
    ${s} .cell, ${s} .pct, ${s} .lbl { opacity: 0.38; }
    ${keep} { opacity: 1; }
    ${lv(0)} { opacity: ${LEVELS[0]}; }
    ${lv(1)} { opacity: ${LEVELS[1]}; }
    ${lv(2)} { opacity: ${LEVELS[2]}; }
    ${s} .hit:hover { opacity: 1; }
    ${s} .m.c${col} { background: ${T.brand}; color: #fff; }
  `;
};

// ─── Balão (isolado: atualizar o balão não toca na grade) ────────────────────

const tipUp = keyframes`
  from { opacity: 0; transform: translate(-50%, calc(-100% + 4px)); }
  to { opacity: 1; transform: translate(-50%, -100%); }
`;
const tipDown = keyframes`
  from { opacity: 0; transform: translate(-50%, -4px); }
  to { opacity: 1; transform: translate(-50%, 0); }
`;

const TipBox = styled.div`
  position: fixed;
  z-index: 3000;
  width: 236px;
  padding: 11px 13px 10px;
  border-radius: 14px;
  pointer-events: none;
  font-family: ${SANS};
  font-variant-numeric: tabular-nums;
  -webkit-font-smoothing: antialiased;
  font-size: 12.5px;
  color: ${({ theme }) => theme.text};
  background: ${({ theme }) => (dk(theme) ? "#2c2c30" : "#ffffff")};
  box-shadow: 0 0 0 0.5px ${({ theme }) => hairline(theme, true)}, 0 14px 32px -10px rgba(0,0,0,0.45), 0 2px 6px rgba(0,0,0,0.12);
  transform: translate(-50%, -100%);
  animation: ${tipUp} 140ms ease-out;
  &.below { transform: translate(-50%, 0); animation-name: ${tipDown}; }
  @media (prefers-reduced-motion: reduce) { animation: none; }

  .arrow {
    position: absolute;
    bottom: -5px;
    width: 10px;
    height: 10px;
    margin-left: -5px;
    transform: rotate(45deg);
    border-radius: 2px;
    background: inherit;
  }
  &.below .arrow { bottom: auto; top: -5px; }

  .k { font-size: 0.86em; color: ${({ theme }) => tokens.label2(theme)}; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .t { margin-top: 1px; font-size: 1.12em; font-weight: 600; letter-spacing: -0.02em; }
  .rows { margin-top: 8px; display: flex; flex-direction: column; gap: 4px; }
  .row { display: flex; justify-content: space-between; gap: 10px; }
  .row span:first-child { color: ${({ theme }) => tokens.label2(theme)}; display: inline-flex; align-items: center; gap: 6px; }
  .row span:last-child { font-weight: 500; }
  .sw { width: 7px; height: 7px; border-radius: 2px; display: inline-block; }
  .act {
    margin-top: 9px;
    padding-top: 8px;
    border-top: 0.5px solid ${({ theme }) => hairline(theme, true)};
    display: flex;
    align-items: center;
    justify-content: space-between;
    color: ${T.brand};
    font-weight: 600;
  }
`;

function tipContent(info, covered) {
  const rep = covered.get(info.key);
  if (info.kind === "hit" && info.hit) {
    return (
      <>
        <div className="k">{branchLabel(info.cnpj)} · {formatCnpj(info.cnpj)}</div>
        <div className="t">{formatMonthKey(info.key, true)}</div>
        <div className="rows">
          <div className="row"><span><i className="sw" style={{ background: GREEN }} />Documentos no SPED</span><span>{formatCount(info.hit.docs)}</span></div>
          {info.hit.nfes > 0 && <div className="row"><span>NF-e</span><span>{formatCount(info.hit.nfes)}</span></div>}
          {info.hit.ctes > 0 && <div className="row"><span>CT-e</span><span>{formatCount(info.hit.ctes)}</span></div>}
          <div className="row"><span><i className="sw" style={{ background: conformityColor(rep.pct) }} />Com XML no mês</span><span>{formatPercent(rep.pct)}</span></div>
        </div>
        <div className="act"><span>Abrir análise</span><FiChevronRight /></div>
      </>
    );
  }
  if (info.kind === "miss") {
    return (
      <>
        <div className="k">{branchLabel(info.cnpj)} · {formatCnpj(info.cnpj)}</div>
        <div className="t">{formatMonthKey(info.key, true)}</div>
        <div className="rows"><div className="row"><span>Sem SPED deste estabelecimento na análise</span></div></div>
        <div className="act"><span>Abrir análise do mês</span><FiChevronRight /></div>
      </>
    );
  }
  if (info.kind === "gap") {
    return (
      <>
        <div className="k">Mês sem análise</div>
        <div className="t">{formatMonthKey(info.key, true)}</div>
        <div className="act"><span>Criar análise deste mês</span><FiPlus /></div>
      </>
    );
  }
  return (
    <>
      <div className="k">Análise do mês</div>
      <div className="t">{formatMonthKey(info.key, true)}</div>
      <div className="rows">
        <div className="row"><span><i className="sw" style={{ background: GREEN }} />Com XML</span><span>{formatCount(rep.encontradas)}</span></div>
        <div className="row"><span><i className="sw" style={{ background: RED }} />Faltantes</span><span>{formatCount(rep.faltantes)}</span></div>
        <div className="row"><span>Cobertura</span><span style={{ color: conformityColor(rep.pct) }}>{formatPercent(rep.pct)}</span></div>
      </div>
      <div className="act"><span>Abrir análise</span><FiChevronRight /></div>
    </>
  );
}

const Tip = forwardRef(function Tip({ covered }, ref) {
  const [state, setState] = useState(null);
  useImperativeHandle(ref, () => ({ show: setState, hide: () => setState(null) }), []);
  if (!state) return null;
  const { info, x, y, below, arrow } = state;
  return createPortal(
    <TipBox key={`${info.kind}-${info.key}-${info.row}`} className={below ? "below" : ""} style={{ left: x, top: y }}>
      {tipContent(info, covered)}
      <span className="arrow" style={{ left: `calc(50% + ${arrow}px)` }} />
    </TipBox>,
    document.body
  );
});

// ─── Grade (memorizada: só renderiza quando os dados mudam) ──────────────────

const GridBody = memo(function GridBody({ timeline, companies, covered, maxDocs, years }) {
  const rows = companies.length;
  return (
    <>
      {years.map((y) => (
        <div key={y.y} className="y" style={{ gridColumn: `${y.start + 2} / span ${y.span}` }}>{y.y}</div>
      ))}
      {timeline.map((k, i) => (
        <div key={`m-${k}`} className={`m c${i}${covered.has(k) ? "" : " gap"}`} style={{ gridColumn: i + 2 }}>
          {MONTHS[Number(k.slice(5)) - 1]}
        </div>
      ))}

      {companies.map((c, r) => (
        <React.Fragment key={c.cnpj}>
          <div className={`lbl r${r}`} style={{ gridRow: r + 3, gridColumn: 1 }} title={formatCnpj(c.cnpj)}>
            <span className="b">{branchLabel(c.cnpj)}</span>
            <span className="c">{formatCnpj(c.cnpj).slice(11)}</span>
          </div>
          {timeline.map((k, i) => {
            const style = { gridRow: r + 3, gridColumn: i + 2 };
            const label = formatMonthKey(k, true);
            if (!covered.has(k)) {
              return (
                <button key={k} type="button" className={`cell gap-cell c${i} r${r}`} style={style} data-k="gap" data-i={i} data-r={r} data-key={k} aria-label={`${label}: sem análise. Criar análise`}>
                  <FiPlus />
                </button>
              );
            }
            const hit = c.months.get(k);
            return (
              <button
                key={k}
                type="button"
                className={hit ? `cell hit l${level(hit.docs, maxDocs)} c${i} r${r}` : `cell miss c${i} r${r}`}
                style={style}
                data-k={hit ? "hit" : "miss"}
                data-i={i}
                data-r={r}
                data-key={k}
                data-cnpj={c.cnpj}
                aria-label={`${branchLabel(c.cnpj)}, ${label}: ${hit ? `${formatCount(hit.docs)} documentos` : "sem SPED"}. Abrir análise`}
              />
            );
          })}
        </React.Fragment>
      ))}

      <div className="divider" style={{ gridRow: rows + 3 }} />
      <div className="lbl" style={{ gridRow: rows + 4, gridColumn: 1, alignItems: "flex-start", paddingTop: 7 }}>
        <span className="c">Com XML</span>
      </div>
      {timeline.map((k, i) => {
        const rep = covered.get(k);
        if (!rep) return <span key={`p-${k}`} style={{ gridRow: rows + 4, gridColumn: i + 2 }} />;
        const color = conformityColor(rep.pct);
        return (
          <button
            key={`p-${k}`}
            type="button"
            className={`pct c${i}`}
            style={{ gridRow: rows + 4, gridColumn: i + 2, color }}
            data-k="pct"
            data-i={i}
            data-key={k}
            aria-label={`${formatMonthKey(k, true)}: ${formatPercent(rep.pct)} dos documentos com XML. Abrir análise`}
          >
            {Math.round(rep.pct)}
            <span className="track"><b style={{ width: `${rep.pct}%`, background: color }} /></span>
          </button>
        );
      })}
    </>
  );
});

// ─── Legenda ─────────────────────────────────────────────────────────────────

const Legend = styled.div`
  margin-top: 14px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px 22px;
  flex-wrap: wrap;
  font-size: 0.74em;
  color: ${({ theme }) => tokens.label2(theme)};

  .grp { display: inline-flex; align-items: center; gap: 16px; flex-wrap: wrap; }
  .it { display: inline-flex; align-items: center; gap: 7px; }
  .sq { width: 12px; height: 12px; border-radius: 4px; display: inline-block; position: relative; }
  .scale { display: inline-flex; gap: 3px; }
  .sq.miss { background: ${neutral}; }
  .sq.miss::after { content: ""; position: absolute; left: 3px; right: 3px; top: 50%; height: 1.5px; margin-top: -0.75px; border-radius: 1px; background: ${({ theme }) => tokens.label3(theme)}; }
  .sq.gap { background: ${quiet}; box-shadow: inset 0 0 0 1px ${({ theme }) => hairline(theme)}; }
`;

let scopeSeq = 0;

export default function CoverageMap({ data, onOpenReport, onCreateMonth }) {
  const { timeline, companies, covered, maxDocs } = data;
  const scope = useMemo(() => { scopeSeq += 1; return `cm${scopeSeq}`; }, []);
  const gridRef = useRef(null);
  const bandRef = useRef(null);
  const styleRef = useRef(null);
  const tipRef = useRef(null);
  const current = useRef(null);

  const years = useMemo(() => {
    const out = [];
    timeline.forEach((k, i) => {
      const y = k.slice(0, 4);
      if (!out.length || out[out.length - 1].y !== y) out.push({ y, start: i, span: 1 });
      else out[out.length - 1].span += 1;
    });
    return out;
  }, [timeline]);

  const byCnpj = useMemo(() => new Map(companies.map((c) => [c.cnpj, c])), [companies]);

  const clear = useCallback(() => {
    if (current.current === null) return;
    current.current = null;
    if (styleRef.current) styleRef.current.textContent = "";
    if (bandRef.current) bandRef.current.classList.remove("on");
    tipRef.current?.hide();
  }, []);

  // Foco numa célula: CSS de destaque, faixa da coluna e balão, sem re-render da grade
  const focus = useCallback((el) => {
    const i = Number(el.dataset.i);
    const r = el.dataset.r != null ? Number(el.dataset.r) : null;
    const id = `${el.dataset.k}-${i}-${r}`;
    if (current.current === id) return;
    current.current = id;

    if (styleRef.current) styleRef.current.textContent = focusCss(scope, i, r);

    const grid = gridRef.current;
    const band = bandRef.current;
    const head = grid.querySelector(`.m.c${i}`);
    const last = grid.querySelector(`.pct.c${i}`) || grid.querySelector(`.cell.c${i}.r${companies.length - 1}`);
    const g = grid.getBoundingClientRect();
    const rect = el.getBoundingClientRect();
    if (band && head && last) {
      const top = head.getBoundingClientRect().top - g.top - 3;
      const bottom = last.getBoundingClientRect().bottom - g.top + 3;
      band.style.transform = `translate(${rect.left - g.left - 2}px, ${top}px)`;
      band.style.width = `${rect.width + 4}px`;
      band.style.height = `${bottom - top}px`;
      band.classList.add("on");
    }

    const cx = rect.left + rect.width / 2;
    const x = Math.min(Math.max(cx, TIP_HALF + 8), window.innerWidth - TIP_HALF - 8);
    const below = rect.top < TIP_ROOM;
    const { cnpj } = el.dataset;
    tipRef.current?.show({
      info: { kind: el.dataset.k, key: el.dataset.key, row: r, cnpj, hit: cnpj ? byCnpj.get(cnpj)?.months.get(el.dataset.key) : null },
      x,
      y: below ? rect.bottom + 9 : rect.top - 9,
      below,
      arrow: Math.max(-TIP_HALF + 14, Math.min(TIP_HALF - 14, cx - x)),
    });
  }, [scope, companies.length, byCnpj]);

  const onOver = useCallback((e) => {
    const el = e.target.closest?.("[data-k]");
    if (el && gridRef.current?.contains(el)) focus(el);
  }, [focus]);

  const onClick = useCallback((e) => {
    const el = e.target.closest?.("[data-k]");
    if (!el) return;
    const key = el.dataset.key;
    clear();
    if (el.dataset.k === "gap") onCreateMonth(key);
    else onOpenReport(covered.get(key));
  }, [covered, onCreateMonth, onOpenReport, clear]);

  // Rolar a página tira o balão de lugar: fecha
  useEffect(() => {
    window.addEventListener("scroll", clear, { capture: true, passive: true });
    return () => window.removeEventListener("scroll", clear, { capture: true });
  }, [clear]);

  // Dados novos (ou desmontagem): limpa o foco
  useEffect(() => clear, [clear, data]);

  return (
    <div onMouseLeave={clear}>
      <style ref={styleRef} />
      <Scroller>
        <Grid
          ref={gridRef}
          className={scope}
          $cols={timeline.length}
          onMouseOver={onOver}
          onFocus={onOver}
          onBlur={clear}
          onClick={onClick}
        >
          <div ref={bandRef} className="band" />
          <GridBody timeline={timeline} companies={companies} covered={covered} maxDocs={maxDocs} years={years} />
        </Grid>
      </Scroller>
      <Tip ref={tipRef} covered={covered} />

      <Legend>
        <span className="grp">
          <span className="it">
            Menos
            <span className="scale">
              {LEVELS.map((o) => <i key={o} className="sq" style={{ background: GREEN, opacity: o }} />)}
            </span>
            Mais documentos
          </span>
        </span>
        <span className="grp">
          <span className="it"><i className="sq miss" />Sem SPED do estabelecimento</span>
          <span className="it"><i className="sq gap" />Mês sem análise · clique para criar</span>
        </span>
      </Legend>
    </div>
  );
}
